import { ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges, inject } from '@angular/core';

export type FlightTicketTone = 'active' | 'cancelled' | 'inactive';

export type CityLookup = (iata: string) => string | null | undefined;

/** Delay before the single retry of a photo whose first load failed. */
export const IMAGE_RETRY_MS = 1500;

/**
 * Visual shell of a flight ticket: photo header with overlay, route headline,
 * die-cut notches + perforation, and a white body where each card projects its content.
 * It never decides which image to show: callers pass `imageUrl` (or nothing, which renders the fallback).
 */
@Component({
  selector: 'app-flight-ticket-frame',
  templateUrl: './flight-ticket-frame.component.html',
  styleUrls: ['./flight-ticket-frame.component.scss'],
  standalone: false
})
export class FlightTicketFrameComponent implements OnChanges, OnDestroy {
  private readonly changeDetector = inject(ChangeDetectorRef);

  @Input() imageUrl?: string | null;
  @Input() imageAlt = '';
  /** Airport codes in travel order, e.g. ['BSB', 'GRU', 'GRU', 'GIG']; consecutive repeats are merged. */
  @Input() stops: readonly string[] = [];
  @Input() connections = 0;
  /** Replaces the connection badge text (e.g. "Ida e volta"). */
  @Input() label?: string | null;
  /** City name for an airport code. Codes without a city are not repeated under the route. */
  @Input() cityOf?: CityLookup | null;
  @Input() departureTime?: string | null;
  @Input() arrivalTime?: string | null;
  @Input() arrivesNextDay = false;
  /** Real connection stops. Empty for a direct flight, which shows "Voo direto" instead. */
  @Input() connectionStops: readonly { code: string; span: string }[] = [];
  @Input() tone: FlightTicketTone = 'active';
  /** Lower photo and tighter body for result lists where several tickets must fit on screen. */
  @Input() compact = false;
  /**
   * No photo at all: the route is drawn on the white surface and nothing is requested for the header.
   * Search results use it on every width; the booked tickets of Meus Voos keep the photo.
   */
  @Input() plain = false;
  /**
   * Confirmation page: photo ticket with the leg label and date on top, the city under the code
   * and one pill for the connection. Presentation only; the inputs above are read the same way.
   */
  @Input() summary = false;

  /** Emits the url of an image that failed to load, right before switching to the fallback. */
  @Output() imageError = new EventEmitter<string>();

  imageFailed = false;
  private retried = false;
  private retryTimer?: ReturnType<typeof setTimeout>;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['imageUrl']) {
      this.imageFailed = false;
      this.retried = false;
      clearTimeout(this.retryTimer);
    }
  }

  ngOnDestroy(): void {
    clearTimeout(this.retryTimer);
  }

  get showImage(): boolean {
    return !this.plain && !!this.imageUrl && !this.imageFailed;
  }

  /** Origin and final arrival; the full path when they coincide so the route never reads "SCL ✈ SCL". */
  get routeCodes(): readonly string[] {
    const stops = (this.stops ?? []).filter((code, i, all) => !!code && code !== all[i - 1]);
    if (stops.length <= 2) return stops;

    const first = stops[0];
    const last = stops[stops.length - 1];
    return first === last ? stops : [first, last];
  }

  get routeLabel(): string {
    return this.routeCodes.join(' para ');
  }

  /**
   * The two codes drawn at hero size. A palindrome round trip (SCL→GIG→SCL) uses the turnaround
   * so the headline reads "SCL → GIG"; a non-palindrome loop (GRU→FOR→MAO→GRU) shows the real
   * last arrival so the headline reads "GRU → GRU". Non-loops always show [first, last].
   */
  get heroCodes(): readonly string[] {
    const codes = this.routeCodes;
    if (codes.length <= 2) return codes;

    const first = codes[0];
    const last = codes[codes.length - 1];
    if (first !== last) return [first, last];

    // Origin === final arrival: palindrome round trips show the turnaround; other loops show both endpoints.
    const isPalindrome = codes.length % 2 === 1 && codes.every((code, i) => code === codes[codes.length - 1 - i]);
    return isPalindrome ? [first, codes[Math.floor((codes.length - 1) / 2)]] : [first, last];
  }

  /** Connection airports that are not already the hero destination. */
  get trackStops(): readonly { code: string; span: string }[] {
    const destination = this.heroCodes.length > 1 ? this.heroCodes[this.heroCodes.length - 1] : '';
    return (this.connectionStops ?? []).filter(stop => stop.code !== destination);
  }

  get originCity(): string {
    return this.cityFor(this.heroCodes[0]);
  }

  get destinationCity(): string {
    const codes = this.heroCodes;
    return codes.length > 1 ? this.cityFor(codes[codes.length - 1]) : '';
  }

  private cityFor(code: string | undefined): string {
    if (!code || !this.cityOf) return '';
    return this.cityOf(code)?.trim() || '';
  }

  get connectionLabel(): string {
    if (this.label) return this.label;
    if (this.connections <= 0) return 'Voo direto';
    return this.connections === 1 ? '1 Conexão' : `${this.connections} Conexões`;
  }

  /** Fallback right away; one later retry covers a transient failure of this request only. */
  onImageError(): void {
    this.imageFailed = true;
    if (this.imageUrl) this.imageError.emit(this.imageUrl);
    if (this.retried) return;

    this.retried = true;
    this.retryTimer = setTimeout(() => {
      this.imageFailed = false;
      this.changeDetector.markForCheck();
    }, IMAGE_RETRY_MS);
  }
}
